import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

import { get } from "@/api";
import { Models } from "@/api/types";
import { convertToFullName } from "@/lib/utils";
import { useUser } from "@/modules/user/useUser/useUser";
import { formatPhoneNumber } from "@/toolbox/PhoneNumber";

import { GiftCardPreview } from "../GiftCardPreview/GiftCardPreview";
import {
  GiftCardFormData,
  GiftCardPurchaseForm,
} from "../GiftCardPurchaseForm/GiftCardPurchaseForm";
import { DiscountMap } from "../giftCards.types";
import { convertGiftCardItemsDiscountsToMap } from "../giftCards.utils";

type GiftCardsPurchaseContainerProps = {
  giftCards: Models<"DisplayGiftCardListing">[];
  platform: Models<"PlatformModel">;
  giftCardListingId?: string | null;
  giftCardItemId?: string | null;
  sharingPersonFirstName?: string | null;
  sharingPersonLastName?: string | null;
  sharingPersonPhone?: string | null;
  sharingPersonEmail?: string | null;
  intendedBuyerUser?: Models<"UserModel1"> | null;
  giftCardItemsDiscounts?: Models<"DisplayGiftCardItemsDiscounts">[];
};

export const GiftCardsPurchaseContainer = ({
  giftCards,
  giftCardListingId,
  giftCardItemId,
  sharingPersonFirstName,
  sharingPersonLastName,
  sharingPersonPhone,
  sharingPersonEmail,
  intendedBuyerUser,
  platform,
  giftCardItemsDiscounts,
}: GiftCardsPurchaseContainerProps) => {
  const { user: currentUser } = useUser();

  // Use current user if available, otherwise fall back to intendedBuyerUser
  const authenticatedUser = currentUser || intendedBuyerUser;
  const senderPhoneNumber = authenticatedUser?.phoneNumber
    ? formatPhoneNumber(authenticatedUser.phoneNumber)
    : "";

  const sharingPersonPhoneFormatted = sharingPersonPhone
    ? formatPhoneNumber(sharingPersonPhone)
    : "";

  const defaultValues: GiftCardFormData = useMemo(() => {
    // For unauthenticated users, set default values for gift card design and amount
    const defaultGiftCardListingId =
      giftCardListingId ?? giftCards[0]?.id ?? "";
    const defaultGiftCardItem =
      giftCards
        .find((giftCard) => giftCard.id === defaultGiftCardListingId)
        ?.giftCards.find((giftCard) => giftCard.packageId === giftCardItemId) ??
      giftCards
        .find((giftCard) => giftCard.id === defaultGiftCardListingId)
        ?.giftCards.sort((a, b) => a.baseCostInCents - b.baseCostInCents)[0];

    return {
      recipientType: {
        value: "someone-else",
        label: "Someone Else",
      },
      senderFirstName: authenticatedUser?.firstName || "",
      senderLastName: authenticatedUser?.lastName || "",
      senderPhoneNumber: senderPhoneNumber,
      senderEmail: authenticatedUser?.email || "",
      recipientFirstName: sharingPersonFirstName ?? "",
      recipientLastName: sharingPersonLastName ?? "",
      recipientPhoneNumber: sharingPersonPhoneFormatted,
      recipientEmail: sharingPersonEmail ?? "",
      message: "",
      giftCardListingId: defaultGiftCardListingId,
      amountInCents: defaultGiftCardItem?.baseCostInCents ?? 0,
      deliveryType: {
        value: "immediate",
        label: "Immediate",
      },
      deliveryDate: undefined,
      deliveryTime: undefined,
    };
  }, [
    authenticatedUser?.firstName,
    authenticatedUser?.lastName,
    authenticatedUser?.email,
    senderPhoneNumber,
    sharingPersonFirstName,
    sharingPersonLastName,
    sharingPersonPhoneFormatted,
    sharingPersonEmail,
    giftCardListingId,
    giftCardItemId,
    giftCards,
  ]);

  const [previewData, setPreviewData] =
    useState<GiftCardFormData>(defaultValues);

  // Update form data when user becomes authenticated
  useEffect(() => {
    if (currentUser && !intendedBuyerUser) {
      // User became authenticated after phone verification
      setPreviewData((prevData) => ({
        ...prevData,
        senderFirstName: currentUser.firstName || prevData.senderFirstName,
        senderLastName: currentUser.lastName || prevData.senderLastName,
        senderPhoneNumber: currentUser.phoneNumber
          ? formatPhoneNumber(currentUser.phoneNumber)
          : prevData.senderPhoneNumber,
        senderEmail: currentUser.email || prevData.senderEmail,
      }));
    }
  }, [currentUser, intendedBuyerUser]);

  const handleFormChange = (data: GiftCardFormData) => {
    setPreviewData(data);
  };

  const handleFormDataUpdate = (data: GiftCardFormData) => {
    // Update form data after phone verification
    setPreviewData(data);
  };

  const selectedGiftCardListing = giftCards.find(
    (giftCard) =>
      giftCard.id === (giftCardListingId || previewData.giftCardListingId)
  );

  const imageUri = selectedGiftCardListing?.imageUri;

  const giftCardItem = selectedGiftCardListing?.giftCards.find(
    (giftCard) => giftCard.baseCostInCents === previewData.amountInCents
  );

  const giftCardValue = giftCardItem?.baseCostInCents;

  // Fetch user-specific promotions when authenticated
  const userPromotionsQueries = useQuery({
    queryKey: [
      "/shop/discounts/listings/all",
      giftCards.map((gc) => gc.id),
      authenticatedUser?.id,
    ],
    queryFn: async () => {
      if (!authenticatedUser) return null;

      const discountPromises: Promise<DiscountMap>[] = giftCards.map((d) =>
        get("/shop/discounts/listings/{listingId}", {
          listingId: d.id,
          includeInclusionsAndRequirementsPackageNames: true,
        }).then((res) => res.packageDiscounts ?? {})
      );

      const results: DiscountMap[] = await Promise.all(discountPromises);
      return Object.assign({}, ...results);
    },
    enabled: !!authenticatedUser,
    staleTime: 1000 * 60 * 2,
  });

  const availablePromotions = useMemo(() => {
    // Use user-specific promotions if authenticated
    if (authenticatedUser && userPromotionsQueries.data) {
      return userPromotionsQueries.data;
    }
    // Fall back to platform-wide discounts for unauthenticated users
    return convertGiftCardItemsDiscountsToMap(giftCardItemsDiscounts || []);
  }, [authenticatedUser, userPromotionsQueries.data, giftCardItemsDiscounts]);

  return (
    <div className="GiftCardsPurchaseContainer relative flex h-auto min-h-[calc(100vh-64px)] flex-col overflow-hidden lg:h-[calc(100vh-64px)] lg:flex-row">
      <motion.div
        className="top-0 flex items-center justify-center bg-brand-color/5 lg:sticky lg:h-screen lg:w-1/2 lg:flex-1"
        initial={{ opacity: 0, x: 0 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeInOut", delay: 0.4 }}
      >
        <GiftCardPreview
          preview={{
            image: imageUri ?? "",
            valueInCents: giftCardValue ?? 0,
          }}
          recipientName={convertToFullName(
            previewData.recipientFirstName,
            previewData.recipientLastName
          )}
          senderName={convertToFullName(
            previewData.senderFirstName,
            previewData.senderLastName
          )}
          platformLogo={platform?.logoImageUrl ?? ""}
          isSomeoneElse={previewData.recipientType.value === "someone-else"}
        />
      </motion.div>
      <motion.div
        className="flex flex-1 justify-center bg-gray-50 lg:h-full lg:overflow-y-auto lg:p-6"
        initial={{ opacity: 0, x: 0 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeInOut", delay: 0.2 }}
      >
        <div className="py-4 sm:max-w-xl">
          <GiftCardPurchaseForm
            defaultValues={defaultValues}
            giftCards={giftCards}
            onChange={handleFormChange}
            onFormDataUpdate={handleFormDataUpdate}
            linkToCart="/gift-cards/cart"
            purchaseSource="PublicPage"
            platform={platform}
            isAuthenticated={!!authenticatedUser}
            availablePromotions={availablePromotions}
          />
        </div>
      </motion.div>
    </div>
  );
};
